import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@ngneat/transloco';
import { fuseAnimations } from '@fuse/animations/public-api';
import {
  FuseNavigationItem,
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { UserService } from 'app/core/user/user.service';
import { UserRole } from 'app/core/user/user.types';
import { GlobalSearchService } from 'app/core/global-search/global-search.service';
import { AnyCategory, MenuCategory, MenuHit, SearchHit, SearchTabKey } from 'app/core/global-search/global-search.types';

@Component({
  selector: 'global-search',
  templateUrl: './global-search.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: fuseAnimations,
  standalone: true,
  imports: [
    DecimalPipe,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    TranslocoModule,
  ],
})
export class GlobalSearchComponent implements OnDestroy {
  @ViewChild('searchInput') private _searchInput!: ElementRef<HTMLInputElement>;

  private readonly _router = inject(Router);
  private readonly _userService = inject(UserService);
  private readonly _fuseNavigationService = inject(FuseNavigationService);
  readonly searchService = inject(GlobalSearchService);

  /** Whether the bar is expanded (visible input) */
  readonly opened = signal(false);

  readonly tabs: { key: SearchTabKey; labelKey: string }[] = [
    { key: 'all', labelKey: 'globalSearch.tabs.all' },
    { key: 'events', labelKey: 'globalSearch.tabs.events' },
    { key: 'vehicles', labelKey: 'globalSearch.tabs.vehicles' },
    { key: 'equipment', labelKey: 'globalSearch.tabs.equipment' },
    { key: 'menus', labelKey: 'globalSearch.tabs.menus' },
  ];

  /**
   * All categories including client-side menu category.
   * Recomputes whenever results or the current query changes.
   */
  readonly allCategories = computed<AnyCategory[]>(() => {
    const backendCategories = this.searchService.results()?.categories ?? [];
    const query = this.searchService.query().trim().toLowerCase();
    const menuCategory = this._buildMenuCategory(query);
    return [...backendCategories, menuCategory];
  });

  /**
   * Categories filtered by the active tab.
   * 'all' shows everything; other tabs show only matching category.
   */
  readonly visibleCategories = computed<AnyCategory[]>(() => {
    const tab = this.searchService.activeTab();
    const categories = this.allCategories();
    if (tab === 'all') return categories;
    return categories.filter(c => c.key === tab);
  });

  /** True when there is at least one hit across visible categories */
  readonly hasVisibleHits = computed(() =>
    this.visibleCategories().some(c => c.hits.length > 0)
  );

  /** Derived from current user role: only internals can update/edit */
  private readonly _isClientUser = computed(() => {
    const role = this._userService.currentUser()?.role;
    return role === UserRole.USER;
  });

  // ──────────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ──────────────────────────────────────────────────────────────────────────

  ngOnDestroy(): void {
    this.searchService.reset();
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Public
  // ──────────────────────────────────────────────────────────────────────────

  open(): void {
    if (this.opened()) return;
    this.opened.set(true);
    // Focus after DOM update
    setTimeout(() => this._searchInput?.nativeElement?.focus());
  }

  close(): void {
    if (!this.opened()) return;
    this.opened.set(false);
    this.searchService.reset();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.code === 'Escape') {
      this.close();
    }
  }

  onQueryChange(value: string): void {
    this.searchService.search(value);
  }

  setTab(tab: SearchTabKey): void {
    this.searchService.activeTab.set(tab);
  }

  navigateToHit(hit: SearchHit): void {
    const { entity, id } = hit.deepLink;
    const isClient = this._isClientUser();

    let path: string[];
    switch (entity) {
      case 'vehicle':
        path = ['vehicles', isClient ? 'view' : 'update', id];
        break;
      case 'event':
        path = ['events', isClient ? 'view' : 'update', id];
        break;
      case 'equipment':
        path = ['equipment', isClient ? 'view' : 'update', id];
        break;
      default:
        return;
    }

    this._router.navigate(path);
    this.close();
  }

  navigateToMenu(link: string): void {
    this._router.navigateByUrl(link);
    this.close();
  }

  isMenuCategory(category: AnyCategory): category is MenuCategory {
    return category.key === 'menus';
  }

  /**
   * Translation key for a category header. Derived from `category.key` (a
   * stable identifier) rather than `category.label`, because that label comes
   * from the API already written in Spanish and would stay Spanish under any
   * other active language.
   */
  categoryLabelKey(category: AnyCategory): string {
    return `globalSearch.categories.${category.key}`;
  }

  trackByKey(_: number, item: AnyCategory): string {
    return item.key;
  }

  trackById(_: number, item: { id: string }): string {
    return item.id;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Private helpers
  // ──────────────────────────────────────────────────────────────────────────

  private _buildMenuCategory(query: string): MenuCategory {
    if (!query || query.length < 2) {
      return { key: 'menus', label: 'Menús', total: 0, hits: [] };
    }

    // La nav vertical se registra vía registerComponent(), no storeNavigation(),
    // así que getNavigation() siempre devuelve []. Leemos del componente.
    const navComponent =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        'mainNavigation',
      );
    const navItems = navComponent?.navigation ?? [];

    // Primer link navegable de un item (el propio, o el del primer descendiente).
    const firstLink = (item: FuseNavigationItem): string | null => {
      if (item.link) return item.link;
      for (const child of item.children ?? []) {
        const link = firstLink(child);
        if (link) return link;
      }
      return null;
    };

    // Recorre el árbol. Los menús colapsables (Eventos, Turnos, Dashboard...) no
    // tienen link propio → navegan a su primer submenú. El título del padre va de
    // sublabel para desambiguar (ej. "Empresas" top vs "Empresas" de Facturación).
    type FlatMenu = {
      title: string;
      link: string;
      icon?: string;
      parent?: string;
    };
    const flat: FlatMenu[] = [];
    const walk = (
      items: FuseNavigationItem[],
      parentTitle?: string,
      parentIcon?: string,
    ): void => {
      for (const item of items ?? []) {
        if (item.type === 'divider' || item.type === 'spacer') continue;
        const link = firstLink(item);
        if (item.title && link) {
          flat.push({
            title: item.title,
            link,
            icon: item.icon ?? parentIcon,
            parent: parentTitle,
          });
        }
        if (item.children?.length) {
          walk(item.children, item.title, item.icon ?? parentIcon);
        }
      }
    };
    walk(navItems);

    const seen = new Set<string>();
    const hits: MenuHit[] = [];
    let total = 0;
    for (const item of flat) {
      const matches = item.title.toLowerCase().includes(query);
      if (!matches || seen.has(item.link)) continue;
      seen.add(item.link);
      total++;
      if (hits.length < 6) {
        hits.push({
          id: item.link,
          label: item.title,
          sublabel: item.parent ?? '',
          link: item.link,
          icon: item.icon ?? '',
        });
      }
    }

    return { key: 'menus', label: 'Menús', total, hits };
  }
}
